import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { FiBook, FiCalendar, FiClock, FiSave, FiArchive, FiFilter, FiUsers } = FiIcons;

const CLOAnalysis = () => {
  const { state, dispatch, performanceService, assessmentService, refreshAttainmentTargets } = useApp();
  const [cloData, setCloData] = useState([]);
  const [cloTargets, setCloTargets] = useState({});
  const [suggestions, setSuggestions] = useState([
    'Increase practical exercises for better understanding',
    'Provide more examples during lectures',
    'Conduct additional tutorial sessions'
  ]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [isTargetsLoaded, setIsTargetsLoaded] = useState(false);
  // Add state for class group filtering
  const [studentGroups, setStudentGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState('All Groups');
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Load data when component mounts or course changes
  useEffect(() => {
    if (state.activeCourse) {
      loadExistingData();
      loadAttainmentTargets();
      // Organize students by class groups when component loads
      organizeStudentGroups();
    }
    if (state.studentsData.length > 0) {
      // Set filtered students initially to all students
      setFilteredStudents(state.studentsData);
    }
  }, [state.activeCourse, state.studentsData]);

  // Calculate CLO achievement when necessary data changes
  useEffect(() => {
    if (state.activeCourse && filteredStudents.length > 0 && 
        state.assessmentPlan.length > 0 && isTargetsLoaded) {
      console.log('🔄 Recalculating CLO achievement with current data');
      calculateCLOAchievement();
    }
  }, [filteredStudents, state.assessmentPlan, state.cloStatements, isTargetsLoaded]);

  // Load targets from global state when available
  useEffect(() => {
    console.log('🎯 CLO Analysis: Attainment targets changed in global state', state.attainmentTargets);
    
    if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
      console.log('🎯 CLO Analysis: Setting CLO targets from global state:', state.attainmentTargets.cloTargets);
      setCloTargets(state.attainmentTargets.cloTargets);
      setIsTargetsLoaded(true);
    }
  }, [state.attainmentTargets]);

  const organizeStudentGroups = () => {
    const groups = { 'All Groups': [] };
    
    state.studentsData.forEach(student => {
      const group = student.classGroup || 'Ungrouped';
      const faculty = student.faculty || '';
      
      // Add to All Groups
      groups['All Groups'].push(student);
      
      // Initialize group if it doesn't exist
      if (!groups[group]) {
        groups[group] = [];
      }
      
      // Add student to their group
      groups[group].push(student);
    });
    
    setStudentGroups(groups);
  };

  const switchClassGroup = (groupName) => {
    setActiveGroup(groupName);
    
    if (groupName === 'All Groups') {
      setFilteredStudents(state.studentsData);
    } else if (studentGroups[groupName]) {
      setFilteredStudents(studentGroups[groupName]);
    }
  };

  const loadExistingData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    try {
      // First try to load from the new table
      const { data, error } = await performanceService.getCommentsAndSuggestions(
        state.activeCourse.id,
        'clo_analysis'
      );
      if (data) {
        setComments(data.comments || '');
        setSuggestions(data.suggestions || suggestions);
        console.log('Loaded comments and suggestions from new table:', data);
      } else {
        // If not found in new table, try the old table for backward compatibility
        const oldData = await performanceService.getPerformanceAnalysis(
          state.activeCourse.id,
          'clo_analysis'
        );
        if (!oldData.error && oldData.data) {
          setComments(oldData.data.comments || '');
          if (oldData.data.data?.suggestions) {
            setSuggestions(oldData.data.data.suggestions);
          }
          console.log('Loaded comments from old table (backward compatibility)');
        }
      }
    } catch (error) {
      console.error('Error loading CLO analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttainmentTargets = async () => {
    if (!state.activeCourse) return;
    try {
      console.log('🎯 CLO Analysis: Loading attainment targets');
      
      // First try to get from global state
      if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
        console.log('🎯 CLO Analysis: Using targets from global state:', state.attainmentTargets.cloTargets);
        setCloTargets(state.attainmentTargets.cloTargets);
        setIsTargetsLoaded(true);
        return;
      }

      // If not in global state, refresh from database
      console.log('🎯 CLO Analysis: Refreshing targets from database');
      await refreshAttainmentTargets(state.activeCourse.id);
      
      // Try again from global state after refresh
      if (state.attainmentTargets.cloTargets && Object.keys(state.attainmentTargets.cloTargets).length > 0) {
        console.log('🎯 CLO Analysis: Using refreshed targets:', state.attainmentTargets.cloTargets);
        setCloTargets(state.attainmentTargets.cloTargets);
        setIsTargetsLoaded(true);
      } else {
        // Set default targets of 50% if none exist
        console.log('🎯 CLO Analysis: Setting default targets');
        const defaultTargets = {};
        Object.keys(state.cloStatements || {}).forEach(clo => {
          defaultTargets[clo] = 50;
        });
        setCloTargets(defaultTargets);
        setIsTargetsLoaded(true);
      }
    } catch (error) {
      console.error('Error loading CLO attainment targets:', error);
    }
  };

  const calculateCLOAchievement = () => {
    console.log('🎯 CLO Analysis: Calculating CLO achievement with targets:', cloTargets);
    console.log('🔍 Available assessments:', state.assessmentPlan);
    console.log('🔍 Available students:', filteredStudents);
    
    const cloAchievementData = [];

    // Get unique CLOs from assessment plan
    const uniqueCLOs = [...new Set(state.assessmentPlan.map(assessment => assessment.clo))];
    console.log('🔍 Unique CLOs found in assessments:', uniqueCLOs);

    uniqueCLOs.forEach(cloCode => {
      // Get all assessments mapped to this CLO
      const cloAssessments = state.assessmentPlan.filter(assessment => assessment.clo === cloCode);
      console.log(`🔍 Assessments for ${cloCode}:`, cloAssessments);
      
      if (cloAssessments.length === 0) {
        console.log(`⚠️ No assessments found for ${cloCode}, skipping`);
        return;
      }

      // Calculate total weightage for this CLO
      const totalWeightage = cloAssessments.reduce((sum, assessment) => sum + assessment.marks, 0);
      console.log(`🔍 Total weightage for ${cloCode}: ${totalWeightage}%`);

      let studentsAchievedCLO = 0;
      const totalStudents = filteredStudents.length;

      // Get the target percentage for this CLO (from state or default to 50%)
      const targetPercentage = cloTargets[cloCode] || 50;
      console.log(`🎯 CLO Analysis: Using target ${targetPercentage}% for ${cloCode}`);

      // For each student, check if they achieved this CLO
      filteredStudents.forEach(student => {
        let studentCLOMarks = 0;
        let studentCLOMaxMarks = 0;

        // Calculate student's total marks for this CLO
        cloAssessments.forEach(assessment => {
          const studentMark = student.marks[assessment.id] || 0;
          const maxMark = assessment.marks;
          studentCLOMarks += studentMark;
          studentCLOMaxMarks += maxMark;
        });

        // Calculate percentage for this CLO
        const cloPercentage = studentCLOMaxMarks > 0 ? (studentCLOMarks / studentCLOMaxMarks) * 100 : 0;

        // Student achieves CLO if they meet the target percentage
        if (cloPercentage >= targetPercentage) {
          studentsAchievedCLO++;
        }
      });

      // Calculate achievement percentage
      const achievementPercentage = totalStudents > 0 ? Math.round((studentsAchievedCLO / totalStudents) * 100) : 0;
      console.log(`🔍 Achievement for ${cloCode}: ${achievementPercentage}% (${studentsAchievedCLO}/${totalStudents} students)`);

      cloAchievementData.push({
        clo: cloCode,
        weightage: totalWeightage,
        attainment: achievementPercentage,
        targetPercentage: targetPercentage,
        status: achievementPercentage >= targetPercentage ? 'Achieved' : 'Not Achieved',
        studentsAchieved: studentsAchievedCLO,
        totalStudents: totalStudents
      });
    });

    setCloData(cloAchievementData);
    console.log('🎯 CLO Analysis: Calculated achievement data:', cloAchievementData);
  };

  const addSuggestion = () => {
    setSuggestions([...suggestions, '']);
  };

  const updateSuggestion = (index, value) => {
    const newSuggestions = [...suggestions];
    newSuggestions[index] = value;
    setSuggestions(newSuggestions);
  };

  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const saveAnalysis = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setSaveLoading(true);
    try {
      // Save to the new table
      const { error: newError } = await performanceService.saveCommentsAndSuggestions(
        state.activeCourse.id,
        'clo_analysis',
        comments,
        suggestions
      );

      if (newError) throw new Error(newError);

      // For backward compatibility and to maintain previous functionality,
      // also save to the old table
      const analysisData = {
        cloData,
        suggestions,
        achievementSummary: {
          totalCLOs: cloData.length,
          achievedCLOs: cloData.filter(clo => clo.status === 'Achieved').length,
          notAchievedCLOs: cloData.filter(clo => clo.status === 'Not Achieved').length,
          averageAchievement: cloData.length > 0 ? Math.round(cloData.reduce((sum, clo) => sum + clo.attainment, 0) / cloData.length) : 0,
          totalStudents: cloData.length > 0 ? cloData[0].totalStudents : 0
        }
      };

      const { error } = await performanceService.savePerformanceAnalysis(
        state.activeCourse.id,
        'clo_analysis',
        analysisData,
        comments,
        '', // removed preparedBy
        '' // removed verifiedBy
      );

      if (error) throw new Error(error);

      alert('CLO Achievement Analysis saved successfully!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving CLO Achievement Analysis: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to view CLO Achievement Analysis.</p>
        </div>
      </motion.div>
    );
  }

  if (state.studentsData.length === 0 || state.assessmentPlan.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Insufficient Data</h3>
            <p className="text-gray-600 mb-4">
              To perform CLO Achievement Analysis, you need:
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <h4 className="font-semibold text-yellow-900 mb-2">Required Data:</h4>
              <ul className="text-yellow-800 space-y-1 list-disc list-inside">
                <li>Assessment Plan with CLO mappings</li>
                <li>Student marks data</li>
                <li>CLO statements (optional but recommended)</li>
              </ul>
              <div className="mt-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Assessment Plan:</span>
                  <span className={state.assessmentPlan.length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {state.assessmentPlan.length > 0 ? "✓ Available" : "✗ Missing"}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">Student Data:</span>
                  <span className={state.studentsData.length > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    {state.studentsData.length > 0 ? `✓ ${state.studentsData.length} students` : "✗ No students"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header - Enhanced */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h2 className="text-xl font-bold text-blue-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-700">Faculty:</span>
            <p className="text-blue-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-blue-700">Programme:</span>
            <p className="text-blue-600">{state.activeCourse.programme}</p>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-blue-700" />
            <div>
              <span className="font-medium text-blue-700">Semester:</span>
              <p className="text-blue-600">{state.activeCourse.semester}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-blue-700" />
            <div>
              <span className="font-medium text-blue-700">Session:</span>
              <p className="text-blue-600">{state.activeCourse.session}</p>
            </div>
          </div>
          <div>
            <span className="font-medium text-blue-700">Total Students:</span>
            <p className="text-blue-600">{filteredStudents.length}</p>
          </div>
          <div>
            <span className="font-medium text-blue-700">CLOs Achieved:</span>
            <p className="text-blue-600">
              {cloData.filter(clo => clo.status === 'Achieved').length}/{cloData.length}
            </p>
          </div>
        </div>
      </div>

      {/* Class Group Selection Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Filter by Class Group</h2>
          </div>
          <div className="text-sm text-blue-600 font-medium">
            {Object.keys(studentGroups).length > 0 && (
              <span>{Object.keys(studentGroups).length - 1} group(s) available</span>
            )}
          </div>
        </div>
        
        {/* Class Group Tabs */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {Object.keys(studentGroups).map(groupName => (
              <button
                key={groupName}
                onClick={() => switchClassGroup(groupName)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeGroup === groupName 
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                  <span>{groupName}</span>
                  <span className="ml-2 bg-white text-blue-800 text-xs px-2 py-0.5 rounded-full">
                    {studentGroups[groupName].length}
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Active Group Info */}
          {activeGroup && activeGroup !== 'All Groups' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 inline mr-2" />
                    {activeGroup} Class Group
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Showing CLO analysis for {studentGroups[activeGroup]?.length || 0} students
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          CLO Achievement Analysis
          {activeGroup !== 'All Groups' && (
            <span className="ml-2 text-lg text-blue-600">({activeGroup})</span>
          )}
        </h2>
        
        {/* Debug Information */}
        {cloData.length === 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-semibold text-red-900 mb-2">⚠️ No CLO Data Generated</h4>
            <p className="text-red-800 text-sm mb-3">
              The system couldn't generate CLO achievement data. This could be due to:
            </p>
            <ul className="text-red-800 text-sm list-disc pl-5 space-y-1">
              <li>Assessments without CLO mappings</li>
              <li>Missing student marks data</li>
              <li>CLO targets not properly loaded</li>
            </ul>
            <div className="mt-3">
              <p className="text-red-800 text-sm">
                <strong>Available CLO Targets:</strong> {Object.keys(cloTargets).length > 0 ? 
                  Object.entries(cloTargets).map(([clo, target]) => `${clo}:${target}%`).join(', ') : 
                  'None'}
              </p>
              <p className="text-red-800 text-sm">
                <strong>Assessment CLO Mappings:</strong> {state.assessmentPlan.length > 0 ? 
                  state.assessmentPlan.map(a => `${a.name}:${a.clo || 'None'}`).join(', ') : 
                  'None'}
              </p>
              <p className="text-red-800 text-sm">
                <strong>Targets Loaded:</strong> {isTargetsLoaded ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        )}
        
        {/* Target Settings Info */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-900 mb-2">🎯 Current Target Settings</h4>
          <p className="text-green-800 text-sm mb-3">
            The analysis below uses the target attainment percentages set in the CLO-PLO Mapping page. 
            Students must achieve at least the target percentage in each CLO for it to be considered "Achieved".
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(cloTargets).length > 0 ? (
              Object.entries(cloTargets).map(([clo, target]) => (
                <div key={clo} className="bg-white p-2 rounded border border-green-200">
                  <span className="font-medium text-green-800">{clo}:</span>
                  <span className="ml-2 text-green-600">{target}%</span>
                </div>
              ))
            ) : (
              <div className="col-span-4 p-2 bg-yellow-50 border border-yellow-100 rounded">
                <p className="text-yellow-800">No CLO targets defined. Using default value of 50%.</p>
              </div>
            )}
          </div>
          <p className="text-green-700 text-xs mt-2">
            To modify these targets, go to CLO-PLO Mapping → Target Settings
          </p>
        </div>

        {/* Historical Data Reference */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiArchive} className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">Historical Achievement Data</h4>
          </div>
          <p className="text-blue-800 text-sm">
            To compare current CLO achievement with previous semesters, please refer to the <strong>Archived Reports</strong> section. This provides comprehensive historical data and trends for better analysis.
          </p>
        </div>

        {/* CLO Attainment Summary */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CLO Attainment Summary</h3>
          {cloData.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">No CLO attainment data available. Please check your Assessment Plan CLO mappings and student data.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">CLO</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Assessment Weightage (%)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Target Attainment (%)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Students Achieved (Count)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Achievement Percentage</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cloData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">{item.clo}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.weightage}%</td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {item.targetPercentage}%
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{item.studentsAchieved}/{item.totalStudents}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.attainment}%</td>
                      <td className={`border border-gray-300 px-4 py-2 font-medium ${item.status === 'Achieved' ? 'text-green-600' : 'text-red-600'}`}>
                        {item.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CLO Achievement Analysis Table */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CLO Achievement Analysis</h3>
          {cloData.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">No CLO achievement analysis data available.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">CLO</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">CLO Statement/Description</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Target Attainment (%)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Percentage of Student (Attained Target)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Status of CLO Achievement</th>
                  </tr>
                </thead>
                <tbody>
                  {cloData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">{item.clo}</td>
                      <td className="border border-gray-300 px-4 py-2">
                        {state.cloStatements[item.clo] || 'CLO statement not defined'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {item.targetPercentage}%
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{item.attainment}%</td>
                      <td className={`border border-gray-300 px-4 py-2 font-medium ${item.status === 'Achieved' ? 'text-green-600' : 'text-red-600'}`}>
                        {item.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CLO Achievement Summary Cards */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievement Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800">Achieved CLOs</h4>
              <p className="text-2xl font-bold text-green-600">
                {cloData.filter(clo => clo.status === 'Achieved').length}/{cloData.length}
              </p>
              <p className="text-sm text-green-600">
                {cloData.length > 0 ? Math.round((cloData.filter(clo => clo.status === 'Achieved').length / cloData.length) * 100) : 0}% Success Rate
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800">Not Achieved CLOs</h4>
              <p className="text-2xl font-bold text-red-600">
                {cloData.filter(clo => clo.status === 'Not Achieved').length}/{cloData.length}
              </p>
              <p className="text-sm text-red-600">Need Improvement</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800">Average Achievement</h4>
              <p className="text-2xl font-bold text-blue-600">
                {cloData.length > 0 ? Math.round(cloData.reduce((sum, clo) => sum + clo.attainment, 0) / cloData.length) : 0}%
              </p>
              <p className="text-sm text-blue-600">Current Semester</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800">Total Students</h4>
              <p className="text-2xl font-bold text-purple-600">{filteredStudents.length}</p>
              <p className="text-sm text-purple-600">Analyzed</p>
            </div>
          </div>
        </div>

        {/* Debugging Information */}
        {cloData.length === 0 && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Troubleshooting Information</h3>
            <p className="text-blue-800 text-sm mb-3">
              To resolve the empty tables issue, please check:
            </p>
            <ul className="text-blue-800 text-sm list-disc pl-5 space-y-1">
              <li>Ensure all assessments in your Assessment Plan have CLO mappings</li>
              <li>Verify student marks have been entered for the assessments</li>
              <li>Check that CLO statements are properly defined</li>
              <li>Try refreshing the page or selecting a different course and coming back</li>
            </ul>
          </div>
        )}

        {/* Improvement Recommendations */}
        {cloData.some(clo => clo.status === 'Not Achieved') && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-900 mb-3">⚠️ CLOs Requiring Attention:</h3>
            <div className="space-y-2">
              {cloData.filter(clo => clo.status === 'Not Achieved').map((clo, index) => (
                <div key={index} className="bg-white p-3 rounded border border-red-200">
                  <div className="flex items-start space-x-3">
                    <span className="font-medium text-red-800 min-w-0">{clo.clo}:</span>
                    <div className="flex-1">
                      <p className="text-red-700 text-sm">
                        {state.cloStatements[clo.clo] || 'CLO statement not defined'}
                      </p>
                      <p className="text-red-600 text-xs mt-1">
                        Achievement: {clo.attainment}% ({clo.studentsAchieved}/{clo.totalStudents} students) | Target: {clo.targetPercentage}% | Shortfall: {Math.max(0, clo.targetPercentage - clo.attainment)}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analysis Comments */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Comments</h3>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your detailed analysis and observations on CLO achievement..."
          />
        </div>

        {/* Suggestions for Improvement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggestions for Improvement of CLO Achievement</h3>
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">{index + 1}.</span>
                <input
                  type="text"
                  value={suggestion}
                  onChange={(e) => updateSuggestion(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter suggestion for improvement"
                />
                <button
                  onClick={() => removeSuggestion(index)}
                  className="text-red-600 hover:text-red-800 px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addSuggestion}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Suggestion
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveAnalysis}
            disabled={saveLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saveLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} className="w-4 h-4" />
            )}
            <span>{saveLoading ? 'Saving...' : 'Save CLO Analysis'}</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default CLOAnalysis;